import React, { useRef, useState } from 'react';
import { Play, Pause, Disc3, Tag, Zap, ShieldOff } from 'lucide-react';

const VERSION_LABEL = {
  original: 'ORIGINAL MIX', original_mix: 'ORIGINAL MIX', curated_id: null, remix: 'REMIX', edit: 'EDIT', bootleg: 'BOOTLEG', vip: 'VIP', rework: 'REWORK',
  flip: 'FLIP', mashup: 'MASHUP', extended: 'EXTENDED MIX', radio: 'RADIO EDIT', club: 'CLUB MIX', dub: 'DUB',
  instrumental: 'INSTRUMENTAL', remaster: 'REMASTER', live: 'LIVE', acoustic: 'ACOUSTIC',
};

const PreviewButton = ({ src, small }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  if (!src) return null;
  const toggle = (e) => {
    e.stopPropagation();
    if (!audioRef.current) {
      audioRef.current = new Audio(src);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().catch(() => {}); setPlaying(true); }
  };
  return (
    <button type="button" onClick={toggle} className={`intel-preview-btn ${small ? 'small' : ''}`} data-testid="intel-preview-button" aria-label={playing ? 'Pause preview' : 'Play 30s preview'}>
      {playing ? <Pause size={small ? 12 : 16} /> : <Play size={small ? 12 : 16} />}
      {!small && <span>{playing ? 'Pause' : '30s preview'}</span>}
    </button>
  );
};

// Electronic-intelligence strip for an identified track: version, remixer, label/year, genre chips, other versions.
export const TrackIntel = ({ track }) => {
  if (!track) return null;

  if (track.outsideScope) {
    return (
      <div className="intel-outside-scope" data-testid="intel-outside-scope">
        <ShieldOff size={18} />
        <div>
          <strong>Outside BEATSEEK scope</strong>
          <p>We heard <em>{track.artist} – {track.title}</em>, but it isn't electronic. BEATSEEK only IDs electronic music — your mic worked fine.</p>
        </div>
      </div>
    );
  }
  if (['not_found', 'error', 'rate_limit'].includes(track.id)) return null;

  const version = VERSION_LABEL[track.versionType] || null;
  const chips = [track.canonicalGenre, ...(track.subgenres || [])].filter(Boolean).filter((c, i, a) => a.indexOf(c) === i).slice(0, 4);
  const versions = (track.versions || []).slice(0, 8);

  return (
    <div className="track-intel" data-testid="track-intel">
      {track.community && (
        <div className="intel-community" data-testid="intel-community">
          {track.community.is_reference ? (
            <>
              <strong>COMMUNITY-CURATED ID · {track.community.aligned ? `${track.community.aligned} fingerprint hits` : 'verified'}</strong>
              <span data-testid="intel-community-curated">Accurate info added by @{track.community.registered_by || 'a BeatSeeker'} — the original ID is untouched</span>
            </>
          ) : (
            <>
              <strong>COMMUNITY EDIT INDEX · {track.community.aligned ? `${track.community.aligned} fingerprint hits` : 'verified'}</strong>
              <span>Registered by @{track.community.registered_by || 'a BEATSEEK DJ'}{track.community.release_status ? ` · ${track.community.release_status.replace('_', ' ')}` : ''}</span>
            </>
          )}
          {!track.community.is_reference && (track.community.original_artist || track.basedOn) && (
            <span data-testid="intel-based-on">Based on {track.community.original_artist || track.basedOn?.artist}{(track.community.original_title || track.basedOn?.title) ? ` – ${track.community.original_title || track.basedOn?.title}` : ''}</span>
          )}
          {track.community.link && <a href={track.community.link} target="_blank" rel="noopener noreferrer" data-testid="intel-community-link">Get it from the creator ↗</a>}
        </div>
      )}
      <div className="intel-row">
        {version && <span className={`intel-version v-${track.versionType}`} data-testid="intel-version-badge">{version}</span>}
        {track.remixer && <span className="intel-remixer" data-testid="intel-remixer">by {track.remixer}</span>}
        {track.possible && <span className="intel-version v-bootleg" data-testid="intel-possible-edit">POSSIBLE EDIT / BOOTLEG</span>}
        {(track.label || track.year) && (
          <span className="intel-label" data-testid="intel-label">
            <Disc3 size={12} /> {[track.label, track.year].filter(Boolean).join(' · ')}
          </span>
        )}
        {track.matchedIn != null && <span className="intel-time" data-testid="intel-matched-in"><Zap size={12} /> {track.matchedIn}s</span>}
        <PreviewButton src={track.previewUrl} />
      </div>
      {chips.length > 0 && (
        <div className="intel-chips" data-testid="intel-genre-chips">
          <Tag size={12} />
          {chips.map((c) => <span key={c} className="intel-chip">{c}</span>)}
          {track.genreVerified === false && <span className="intel-chip unverified" title="Genre not yet confirmed by two sources">unverified</span>}
        </div>
      )}
      {versions.length > 0 && (
        <div className="intel-versions" data-testid="intel-versions">
          <p className="intel-versions-title">Other versions</p>
          <div className="intel-versions-rail">
            {versions.map((v) => (
              <a key={v.title} className="intel-version-card" href={v.link || undefined} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!v.link) e.preventDefault(); }} data-testid="intel-version-card">
                {v.artwork ? <img src={v.artwork} alt="" loading="lazy" /> : <div className="intel-version-art-fallback"><Disc3 size={16} /></div>}
                <div className="intel-version-meta">
                  <span className="intel-version-kind">{VERSION_LABEL[v.version_type] || 'VERSION'}</span>
                  <span className="intel-version-name">{v.remixer || v.title}</span>
                </div>
                <PreviewButton src={v.preview} small />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackIntel;
